import {
  GatewaysGatewayIdDeleteRequest,
  GatewaysGatewayIdPatchRequest,
  GatewaysPostRequest,
  OriginGatewayPaginatedList,
} from '@nativewaves/nw-cloud-sdk/origin'
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query'

import { OriginAPI } from 'services/api'
import { gatewayQueryKeys } from 'services/queryKeys'
import { updateListCachesByCreation, updateListCachesByDeletion, updateListCachesByUpdate } from 'utils'

export const gatewayMutationKeys = {
  delete: () => ['origin', 'gateway', 'DELETE'] as const,
}

type UseGatewayCreateMutationProps = {
  originId?: string
}

export const useGatewayCreateMutation = (props: UseGatewayCreateMutationProps) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: GatewaysPostRequest) => OriginAPI.gateways.gatewaysPost(params),
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: gatewayQueryKeys.lists(),
      })
    },
    onSettled: (data, error) => {
      if (!!data && !error) {
        queryClient.setQueriesData<InfiniteData<OriginGatewayPaginatedList>>(
          { queryKey: gatewayQueryKeys.lists() },
          updateListCachesByCreation(data, { idKey: 'id', originId: props.originId }),
        )
      }
    },
  })
}

export const useGatewayUpdateMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: GatewaysGatewayIdPatchRequest) => OriginAPI.gateways.gatewaysGatewayIdPatch(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: gatewayQueryKeys.batchGets() })
    },
    onSettled: (data, error, variables) => {
      if (!error && !!data) {
        queryClient.resetQueries({ queryKey: gatewayQueryKeys.batchGets() })

        queryClient.setQueriesData<InfiniteData<OriginGatewayPaginatedList>>(
          { queryKey: gatewayQueryKeys.lists() },
          updateListCachesByUpdate(data, { idKey: 'id', idToMatch: variables.gatewayId }),
        )

        return queryClient.invalidateQueries({ queryKey: gatewayQueryKeys.details() })
      }
    },
  })
}

export const useGatewayDeleteMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: gatewayMutationKeys.delete(),
    mutationFn: (params: GatewaysGatewayIdDeleteRequest) => OriginAPI.gateways.gatewaysGatewayIdDelete(params),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: gatewayQueryKeys.lists() })
      await queryClient.cancelQueries({ queryKey: gatewayQueryKeys.batchGets() })
    },
    onSettled: (_, error, variables) => {
      if (!error) {
        queryClient.setQueriesData<InfiniteData<OriginGatewayPaginatedList>>(
          { queryKey: gatewayQueryKeys.lists() },
          updateListCachesByDeletion({ idKey: 'id', idToMatch: variables.gatewayId }),
        )
      }
    },
  })
}
